import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const GoogleMyBusinessManagementPage: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-google-fill',
      title: 'Google Map Visibility',
      description: 'Appear in Google Maps when customers search for businesses like yours in your area.'
    },
    {
      icon: 'ri-star-fill',
      title: 'Review Management',
      description: 'Professional review monitoring and response to build trust and credibility.'
    },
    {
      icon: 'ri-image-line',
      title: 'Visual Showcase',
      description: 'High-quality photos and posts that showcase your business and attract customers.'
    },
    {
      icon: 'ri-bar-chart-line',
      title: 'Performance Insights',
      description: 'Detailed analytics on how customers find and interact with your business.'
    }
  ];

  const features = [
    'Complete GMB profile setup',
    'Business information optimization',
    'Photo and video management',
    'Review monitoring & responses',
    'Google Posts creation',
    'Q&A management',
    'Insights and analytics reporting',
    'Local SEO optimization'
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-red-100 to-orange-100 rounded-full px-6 py-2 mb-6">
            <i className="ri-google-fill text-red-600 mr-2"></i>
            <span className="text-red-800 font-medium">Google My Business Management</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600 bg-clip-text text-transparent">
              Maximize Your Google
            </span>
            <br />
            Business Presence
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Professional Google My Business management that helps your small business get found, build trust, and attract more local customers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-gradient-to-r from-red-600 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-white text-orange-600 border-2 border-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-50 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                Why Google My Business is Essential for Small Businesses
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Your Google My Business profile is often the first impression customers have of your business. Make it count with professional management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-red-500 to-orange-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
                  Complete GMB Management
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We handle every aspect of your Google My Business profile to ensure maximum visibility and engagement with potential customers.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-red-400 to-orange-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/9606a1516823b0a7db8c81f6dc3e0cf6.jpg"
                alt="Google My Business Management"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-orange-600 to-yellow-600 bg-clip-text text-transparent">
                The Power of Google My Business
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600 mb-2">5x</div>
              <p className="text-gray-600">more likely to be contacted</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">70%</div>
              <p className="text-gray-600">of consumers visit after searching</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-yellow-600 mb-2">50%</div>
              <p className="text-gray-600">of searches result in store visits</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-pink-600 mb-2">92%</div>
              <p className="text-gray-600">trust online reviews</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-red-600 via-orange-600 to-yellow-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Optimize Your Google Presence?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let us manage your Google My Business profile and help you attract more local customers.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/consultation"
              className="bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-white hover:text-red-200 transition-colors duration-300">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-red-200 transition-colors duration-300">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-red-200 transition-colors duration-300">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-red-200 transition-colors duration-300">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default GoogleMyBusinessManagementPage;
